import React, { useState } from 'react';
import axios from 'axios';
import { Shield, ChevronDown, ChevronRight, Save, RotateCcw } from 'lucide-react';
import { API } from '../../config';

// Hard-coded mirror of the backend's `TAB_ROLE_DEFAULTS` and `PERMISSION_OVERRIDE_TABS`.
// Kept in sync manually (no extra API round-trip per user row). Audit Log is
// intentionally absent — it stays admin-only and is not overridable.
const OVERRIDABLE_TABS = [
  { key: 'dashboard', label: 'Dashboard', roles: ['admin', 'plant_manager', 'viewer', 'operator'] },
  { key: 'entry',     label: 'Data Entry', roles: ['admin', 'plant_manager', 'operator'] },
  { key: 'plants',    label: 'Plant Master', roles: ['admin'] },
  { key: 'rates',     label: 'Manage Rates', roles: ['admin', 'plant_manager'] },
  { key: 'co2_losses', label: 'CO₂ Losses', roles: ['admin', 'plant_manager'] },
  { key: 'users',     label: 'Users', roles: ['admin'] },
  { key: 'approvals', label: 'Approvals', roles: ['admin'] },
  { key: 'emails',    label: 'Emails', roles: ['admin', 'plant_manager'] },
];

const effectiveAccess = (tab, role, overrides) => {
  if (tab.key in overrides) return overrides[tab.key];
  return tab.roles.includes(role);
};

const UserPermissionsPanel = ({ user, token, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [overrides, setOverrides] = useState(user.permission_overrides || {});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const isAdmin = user.role === 'admin';

  const toggle = (tabKey, role) => {
    const roleDefault = OVERRIDABLE_TABS.find(t => t.key === tabKey).roles.includes(role);
    const currentEffective = tabKey in overrides ? overrides[tabKey] : roleDefault;
    const next = { ...overrides };
    if (currentEffective === roleDefault) {
      // Was matching role default — set explicit opposite override.
      next[tabKey] = !roleDefault;
    } else {
      // Was an explicit override — clear it (revert to role default).
      delete next[tabKey];
    }
    setOverrides(next);
  };

  const resetAll = () => setOverrides({});

  const save = async () => {
    try {
      setSaving(true);
      setMsg('');
      await axios.put(
        `${API}/users/${user.id}/permissions`,
        { permission_overrides: overrides },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg('✓ Permissions saved');
      if (onSaved) await onSaved();
    } catch (err) {
      setMsg('✗ ' + (err.response?.data?.detail || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (isAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 px-3 py-2 rounded text-xs text-amber-700" data-testid={`permissions-admin-note-${user.username}`}>
        Admin role has access to every tab — no per-tab override needed.
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-gray-100 rounded-t-lg"
        data-testid={`permissions-toggle-${user.username}`}
      >
        <span className="flex items-center text-sm font-medium text-gray-700">
          <Shield className="h-4 w-4 mr-2 text-[#156ACA]" />
          Advanced Permissions
          {Object.keys(overrides).length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-[#156ACA] text-white text-xs rounded-full">
              {Object.keys(overrides).length} override(s)
            </span>
          )}
        </span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>

      {open && (
        <div className="p-3 border-t border-gray-200" data-testid={`permissions-panel-${user.username}`}>
          <p className="text-xs text-gray-600 mb-3">
            Click any cell to grant or revoke that tab for this user. <strong>Role default</strong> = the row matches the role&apos;s built-in permission.
            Audit Log is admin-only and cannot be overridden.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-2 py-1.5">Tab</th>
                  <th className="text-center px-2 py-1.5">Role Default</th>
                  <th className="text-center px-2 py-1.5">Override</th>
                  <th className="text-center px-2 py-1.5">Effective</th>
                </tr>
              </thead>
              <tbody>
                {OVERRIDABLE_TABS.map(tab => {
                  const roleDefault = tab.roles.includes(user.role);
                  const hasOverride = tab.key in overrides;
                  const effective = hasOverride ? overrides[tab.key] : roleDefault;
                  return (
                    <tr key={tab.key} className="border-t border-gray-200" data-testid={`permissions-row-${user.username}-${tab.key}`}>
                      <td className="px-2 py-1.5 font-medium text-gray-800">{tab.label}</td>
                      <td className="text-center px-2 py-1.5">
                        <span className={roleDefault ? 'text-green-600' : 'text-gray-400'}>{roleDefault ? '✓' : '—'}</span>
                      </td>
                      <td className="text-center px-2 py-1.5">
                        {hasOverride ? (
                          <span className={`inline-block px-2 py-0.5 rounded font-semibold ${overrides[tab.key] ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {overrides[tab.key] ? 'GRANT' : 'REVOKE'}
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="text-center px-2 py-1.5">
                        <button
                          onClick={() => toggle(tab.key, user.role)}
                          className={`inline-block px-3 py-1 rounded-full font-semibold cursor-pointer hover:opacity-80 transition-opacity ${
                            effective ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                          data-testid={`permissions-cell-${user.username}-${tab.key}`}
                        >
                          {effective ? 'Allowed' : 'Blocked'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between">
            {msg ? (
              <span className={`text-xs ${msg.startsWith('✓') ? 'text-green-700' : 'text-red-700'}`}>{msg}</span>
            ) : <span></span>}
            <div className="flex gap-2">
              <button
                onClick={resetAll}
                className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-white flex items-center"
                data-testid={`permissions-reset-${user.username}`}
              >
                <RotateCcw className="h-3 w-3 mr-1" /> Reset to Role Defaults
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="text-xs px-3 py-1.5 bg-[#156ACA] hover:bg-[#0F5299] text-white rounded font-semibold disabled:opacity-50 flex items-center"
                data-testid={`permissions-save-${user.username}`}
              >
                <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissionsPanel;
